import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { CLEANING_STAFF_ROLES } from "@/lib/cleaningStaffRoles";
import { createCoordinatorExtraJob, type ExtraJobPayMode } from "@/lib/extraPaidWork";

export interface ExtraPaidJobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  locations: { id: string; address: string }[];
  presetLocationId?: string;
  onSuccess?: () => void;
}

export function ExtraPaidJobModal({
  open,
  onOpenChange,
  orgId,
  locations,
  presetLocationId,
  onSuccess,
}: ExtraPaidJobModalProps) {
  const { t } = useTranslation();
  const [locationId, setLocationId] = useState(presetLocationId ?? "");
  const [staffId, setStaffId] = useState("");
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [requiresPhoto, setRequiresPhoto] = useState(false);
  const [gpsRequired, setGpsRequired] = useState(true);
  const [qrRequired, setQrRequired] = useState(false);
  const [biometricRequired, setBiometricRequired] = useState(false);
  const [payMode, setPayMode] = useState<ExtraJobPayMode>("hourly");
  const [employeeHourlyRate, setEmployeeHourlyRate] = useState("");
  const [clientHourlyRate, setClientHourlyRate] = useState("");
  const [employeeFixedAmount, setEmployeeFixedAmount] = useState("");
  const [clientFixedAmount, setClientFixedAmount] = useState("");
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && presetLocationId) setLocationId(presetLocationId);
  }, [open, presetLocationId]);

  useEffect(() => {
    if (!open || !orgId) return;
    let cancelled = false;
    const loadStaff = async () => {
      const { data: members } = await supabase
        .from("memberships")
        .select("user_id")
        .eq("org_id", orgId)
        .in("role", [...CLEANING_STAFF_ROLES])
        .eq("is_active", true);
      const ids = [...new Set((members ?? []).map((row) => row.user_id).filter(Boolean))] as string[];
      if (ids.length === 0) {
        if (!cancelled) setStaff([]);
        return;
      }
      const { data: profiles } = await supabase.from("profiles").select("id, full_name").in("id", ids);
      if (!cancelled) {
        setStaff((profiles ?? []).map((profile) => ({ id: profile.id, name: profile.full_name || t("admin.extraJob.workerFallback") })));
      }
    };
    void loadStaff();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, t]);

  const reset = () => {
    setTitle("");
    setInstructions("");
    setStaffId("");
    setRequiresPhoto(false);
    setGpsRequired(true);
    setQrRequired(false);
    setBiometricRequired(false);
    setPayMode("hourly");
    setEmployeeHourlyRate("");
    setClientHourlyRate("");
    setEmployeeFixedAmount("");
    setClientFixedAmount("");
  };

  const handleSubmit = async () => {
    if (!locationId || !staffId || !title.trim()) {
      toast.error(t("admin.extraJob.selectRequired"));
      return;
    }
    if (payMode === "fixed" && !employeeFixedAmount) {
      toast.error(t("admin.extraJob.fixedAmountRequired"));
      return;
    }
    setIsSubmitting(true);
    try {
      await createCoordinatorExtraJob({
        orgId,
        locationId,
        assignedStaffId: staffId,
        title: title.trim(),
        instructions: instructions.trim() || undefined,
        requiresPhoto,
        gpsRequired,
        qrRequired,
        biometricRequired,
        payMode,
        employeeHourlyRate: employeeHourlyRate ? Number(employeeHourlyRate) : null,
        clientHourlyRate: clientHourlyRate ? Number(clientHourlyRate) : null,
        employeeFixedAmount: employeeFixedAmount ? Number(employeeFixedAmount) : null,
        clientFixedAmount: clientFixedAmount ? Number(clientFixedAmount) : null,
      });
      toast.success(t("admin.extraJob.created"));
      reset();
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("admin.extraJob.createFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("admin.extraJob.title")}</DialogTitle>
          <DialogDescription>
            {t("admin.extraJob.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5">
            <Label>{t("admin.extraJob.property")}</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger>
                <SelectValue placeholder={t("admin.extraJob.selectProperty")} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>{t("admin.extraJob.worker")}</Label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger>
                <SelectValue placeholder={t("admin.extraJob.selectWorker")} />
              </SelectTrigger>
              <SelectContent>
                {staff.map((person) => (
                  <SelectItem key={person.id} value={person.id}>
                    {person.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="extra-job-title">{t("admin.extraJob.jobTitle")}</Label>
            <Input id="extra-job-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="extra-job-instructions">{t("admin.extraJob.instructions")}</Label>
            <Textarea id="extra-job-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} />
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">{t("admin.extraJob.verification")}</p>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={gpsRequired} onCheckedChange={(checked) => setGpsRequired(checked === true)} />
              GPS
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={qrRequired} onCheckedChange={(checked) => setQrRequired(checked === true)} />
              {t("admin.extraJob.qrCode")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={biometricRequired} onCheckedChange={(checked) => setBiometricRequired(checked === true)} />
              {t("admin.extraJob.biometrics")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={requiresPhoto} onCheckedChange={(checked) => setRequiresPhoto(checked === true)} />
              {t("admin.extraJob.requirePhoto")}
            </label>
          </div>
          <div className="grid gap-1.5">
            <Label>{t("admin.extraJob.billing")}</Label>
            <Select value={payMode} onValueChange={(value) => setPayMode(value as ExtraJobPayMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">{t("admin.extraJob.hourly")}</SelectItem>
                <SelectItem value="fixed">{t("admin.extraJob.fixed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {payMode === "hourly" ? (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1.5">
                <Label>{t("admin.extraJob.employeeHourly")}</Label>
                <Input type="number" step="0.01" value={employeeHourlyRate} onChange={(e) => setEmployeeHourlyRate(e.target.value)} placeholder={t("admin.extraJob.employeeHourlyPlaceholder")} />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("admin.extraJob.clientHourly")}</Label>
                <Input type="number" step="0.01" value={clientHourlyRate} onChange={(e) => setClientHourlyRate(e.target.value)} placeholder={t("admin.extraJob.clientHourlyPlaceholder")} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1.5">
                <Label>{t("admin.extraJob.employeeAmount")}</Label>
                <Input type="number" step="0.01" value={employeeFixedAmount} onChange={(e) => setEmployeeFixedAmount(e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("admin.extraJob.clientAmount")}</Label>
                <Input type="number" step="0.01" value={clientFixedAmount} onChange={(e) => setClientFixedAmount(e.target.value)} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button disabled={isSubmitting} onClick={() => void handleSubmit()}>
            {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {t("admin.extraJob.addJob")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
